import {
  TRIMBLE_DEVELOPER_MACHINE_NAME,
  TRIMBLE_DEVELOPER_NEWS_BLOG_SLUG,
} from "./trimbleDeveloperNewsConstants"

type RewriteRule = {
  source: string
  destination: string
}

function isTrimbleDeveloperSiteAtBuildTime(): boolean {
  return (
    process.env.NEXT_PUBLIC_SITE_MACHINE_NAME ===
      TRIMBLE_DEVELOPER_MACHINE_NAME ||
    process.env.NEXT_PUBLIC_PROFILE?.startsWith("trimble-developer") === true
  )
}

/** Rules for `next.config.ts` `rewrites()` — trimble-developer builds only.
 *
 * Order matters: Next.js tries rules top-to-bottom and takes the first
 * match, so the specific `/news/tags/*` and `/news/collection/*` rules
 * MUST come before the catch-all `/news/:slug+` → article rule.
 * Otherwise the catch-all would route a tag URL like `/news/tags/foo`
 * into the article page with slug "tags/foo", which 404s.
 *
 * TD news only hosts articles today (no case-study/video/docs/etc. use
 * the `/news/` prefix), so we only need tags + collection here. If other
 * content types ever land under `/news/`, add their rewrites here before
 * the catch-all.
 */
export function getTrimbleDeveloperNewsRewritesForNextConfig(): RewriteRule[] {
  if (!isTrimbleDeveloperSiteAtBuildTime()) {
    return []
  }

  return [
    {
      source: "/:lang/news/tags/:slug+",
      destination: `/:lang/tags/_bp-${TRIMBLE_DEVELOPER_NEWS_BLOG_SLUG}/_et-articles/:slug+`,
    },
    {
      source: "/:lang/news/collection/:slug+",
      destination: `/:lang/collection/_bp-${TRIMBLE_DEVELOPER_NEWS_BLOG_SLUG}/_et-articles/:slug+`,
    },
    {
      source: "/:lang/news/:slug+",
      destination: `/:lang/article/_bp-${TRIMBLE_DEVELOPER_NEWS_BLOG_SLUG}/:slug+`,
    },
  ]
}

/**
 * Map a user-facing TD news articleURL to its internal `_bp-tdnews` route.
 * Returns `null` when the path is not a TD news article URL.
 */
export function getTrimbleDeveloperNewsRewriteDestination(
  pathname: string,
): string | null {
  if (!isTrimbleDeveloperSiteAtBuildTime()) {
    return null
  }

  const tagsMatch = pathname.match(/^\/([^/]+)\/news\/tags\/(.+)$/)
  if (tagsMatch) {
    return `/${tagsMatch[1]}/tags/_bp-${TRIMBLE_DEVELOPER_NEWS_BLOG_SLUG}/_et-articles/${tagsMatch[2]}`
  }

  const collectionMatch = pathname.match(/^\/([^/]+)\/news\/collection\/(.+)$/)
  if (collectionMatch) {
    return `/${collectionMatch[1]}/collection/_bp-${TRIMBLE_DEVELOPER_NEWS_BLOG_SLUG}/_et-articles/${collectionMatch[2]}`
  }

  const articleMatch = pathname.match(/^\/([^/]+)\/news\/(.+)$/)
  if (!articleMatch) {
    return null
  }

  return `/${articleMatch[1]}/article/_bp-${TRIMBLE_DEVELOPER_NEWS_BLOG_SLUG}/${articleMatch[2]}`
}
